/**
 * The TokenDetail container
 */
import React from "react";
// import { useSsrEffect } from "@issr/core";
import { genericAbiRead } from "@bkry/bowline-redux/contracts";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { Card, CardBody, Col, Row, Spinner } from "reactstrap";
// import Page from "../../components/Page";
import {
  balanceOfAbi,
  stakerStatsAbi,
  totalTokensStakedCountAbi,
} from "@bkry/bowline-components/Staking";
import {
  updateStakedTokensData,
  updateTotalTokensStaked,
} from "@bkry/bowline-redux/tokens";
import { useSsrEffect, useSsrState, useRegisterEffect } from "@issr/core";
import { ethers } from "ethers";
import ClaimButton from "./utils/ClaimButtons";
import { useAccount } from "../../CustomHook";

/**
 * TokenDetail container used as app default landing page
 */
const StakingStats = (props, context) => {
  let currencyName = "$REWARD";
  const { stakeData, stakingContract, rewardsContract } = props;
  const dispatch = useDispatch();
  const registerEffect = useRegisterEffect();
  const { account, ethProvider: provider } = useAccount();
  const [balance, setBalance] = useSsrState(0);

  const handleStakingData = async () => {
    registerEffect(
      dispatch,
      genericAbiRead({
        functionAbi: stakerStatsAbi,
        contractAddress: stakingContract,
        account: account,
        provider: provider,
        functionName: "stakerStats(wallet_address)",
        genericInputs: {
          _stakerAddress: account,
        },
        successCallbackFnc: (data) => {
          dispatch(updateStakedTokensData({ ...data }));
          // setStakeData(data);
        },
        errorCallbackFnc: (data) => {
          console.error("error Data", data);
        },
      })
    );
  };

  const handleTotalTokensCountStaked = async () => {
    registerEffect(
      dispatch,
      genericAbiRead({
        functionAbi: totalTokensStakedCountAbi,
        contractAddress: stakingContract,
        account: account,
        provider: provider,
        successCallbackFnc: (data) => {
          dispatch(updateTotalTokensStaked(data.toString()));
        },
        errorCallbackFnc: (data) => {
          console.error("error Data", data);
        },
      })
    );
  };

  const handleBalanceOfKeys = async () => {
    registerEffect(
      dispatch,
      genericAbiRead({
        functionAbi: balanceOfAbi,
        contractAddress: rewardsContract,
        account: account,
        provider: provider,
        genericInputs: {
          tokenHolder: account,
        },
        successCallbackFnc: (data) => {
          setBalance(ethers.utils.formatEther(data));
        },
        errorCallbackFnc: (data) => {
          console.error("error Data", data);
        },
      })
    );
  };

  const refreshStakingData = () => {
    handleStakingData();
    handleTotalTokensCountStaked();
    handleBalanceOfKeys();
  };

  useSsrEffect(() => {
    refreshStakingData();
    const interval = setInterval(() => {
      refreshStakingData();
    }, 13000); //refresh every 13 seconds enabled and available amount
    return () => clearInterval(interval);
  }, []);

  return (
    <Row className="my-5">
      <Col lg={4}>
        <Card
          className={`border-0 rounded p-3 h-100 ${
            !stakeData?.unclaimedRewards ? "opacity-50" : "opacity-100"
          }`}
          style={{ background: "#212529" }}
        >
          <CardBody>
            <p className="text-uppercase">{context.t("unclaimed earnings")}</p>
            <div className="d-flex align-items-center">
              <p className="fw-bold text-white h2">
                {stakeData?.unclaimedRewards ? (
                  ethers.utils.formatEther(stakeData?.unclaimedRewards)
                ) : (
                  <Spinner color="primary" />
                )}
              </p>
              <p className="text-uppercase ms-3 pb-0 mb-0">{currencyName}</p>
            </div>
            <small className="text-muted">
              {context.t("Your retrieved earnings, that are ready to claim!")}
            </small>
            <hr className="mx-4" />
            <p className="text-uppercase mt-4">
              {context.t("pending rewards")}
            </p>
            <div className="d-flex align-items-center">
              <p className="fw-bold text-white h2">
                {stakeData?.unaccountedRewards ? (
                  ethers.utils.formatEther(stakeData?.unaccountedRewards)
                ) : (
                  <Spinner color="primary" />
                )}
              </p>
              <p className="text-uppercase ms-3 pb-0 mb-0">{currencyName}</p>
            </div>
          </CardBody>
          <ClaimButton stakingContract={stakingContract} />
        </Card>
      </Col>
      <Col lg={4}>
        <Card
          className={`border-0 rounded p-3 h-100 ${
            !stakeData?.totalTokensStaked ? "opacity-50" : "opacity-100"
          }`}
          style={{ background: "#212529" }}
        >
          <CardBody>
            <p className="text-uppercase">{context.t("Tokens in the Hole")}</p>
            <p className="fw-bold text-white h2">
              {stakeData?.totalTokensStaked ? (
                stakeData?.totalTokensStaked.toString()
              ) : (
                <Spinner color="primary" />
              )}
            </p>
            <small className="text-muted">
              {context.t("Your total Tokens staked")}
            </small>
          </CardBody>
        </Card>
      </Col>
      <Col lg={4}>
        <Card
          className={`border-0 rounded p-3 h-100 ${
            !stakeData?.lifetimeRewards || !balance
              ? "opacity-50"
              : "opacity-100"
          }`}
          style={{ background: "#212529" }}
        >
          <CardBody>
            <p className="text-uppercase">{context.t("Available Balance")}</p>
            <div className="d-flex align-items-center">
              <p className="fw-bold text-white h2">
                {balance || balance == 0 ? (
                  balance.toLocaleString("en-US")
                ) : (
                  <Spinner color="primary" />
                )}
              </p>
              <p className="ms-3 pb-0 mb-0">{currencyName}</p>
            </div>
            <hr className="mx-4" />
            <div className="d-flex align-items-center mt-4">
              <p className="fw-bold text-white h4">
                {stakeData?.lifetimeRewards ? (
                  ethers.utils.formatEther(stakeData?.lifetimeRewards)
                ) : (
                  <Spinner color="primary" />
                )}
              </p>
              <p className="ms-3 pb-0 mb-0 text-uppercase small">
                total {currencyName}{" "}
              </p>
            </div>
            <small className="text-muted">
              {context.t("Your lifetime earnings from staking.")}
            </small>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

/**  define proptype for the 'translation' function  */
StakingStats.contextTypes = {
  t: PropTypes.func,
};

StakingStats.propTypes = {
  stakeData: PropTypes.array,
  stakingContract: PropTypes.string,
  rewardsContract: PropTypes.string,
};

/** export the component as redux connected component */
export default StakingStats;
